import { useEffect } from 'react'
import {
  getCrispConfig,
  crispAutoResponderConfig,
  crispCustomConfig,
} from '../config/crisp.config'

/**
 * useCrisp Hook
 * 在客户端初始化和配置 Crisp Chat Widget
 *
 * 使用方法:
 * import { useCrisp } from '@/hooks/useCrisp'
 *
 * function MyComponent() {
 *   useCrisp()
 *   return <div>...</div>
 * }
 */
export const useCrisp = () => {
  useEffect(() => {
    // 只在浏览器环境执行
    if (typeof window === 'undefined') {
      return
    }

    // 获取 Crisp 配置 (包含 A/B 测试逻辑)
    const config = getCrispConfig()

    // 如果没有 Website ID，不初始化
    if (!config.websiteId || config.websiteId === 'YOUR_CRISP_WEBSITE_ID') {
      console.warn(
        '⚠️ Crisp Website ID is not configured. Please set GATSBY_CRISP_WEBSITE_ID in your .env file.',
      )
      return
    }

    // 如果已经初始化过，不重复初始化
    if (window.$crisp) {
      return
    }

    // 初始化 Crisp (根据官方文档)
    window.$crisp = []
    window.CRISP_WEBSITE_ID = config.websiteId

    // 应用自定义配置
    if (crispCustomConfig.locale) {
      window.$crisp.push(['set', 'locale', crispCustomConfig.locale])
    }

    if (crispCustomConfig.colorTheme) {
      window.$crisp.push(['set', 'color:theme', [crispCustomConfig.colorTheme]])
    }

    if (crispCustomConfig.position === 'left') {
      window.$crisp.push(['set', 'position:reverse', true])
    }

    if (crispCustomConfig.hideOnMobile) {
      window.$crisp.push(['set', 'hide:on:mobile', true])
    }

    // 加载 Crisp SDK 脚本
    const script = document.createElement('script')
    script.src = 'https://client.crisp.chat/l.js'
    script.async = true
    document.head.appendChild(script)

    // 监听 Crisp 加载完成事件
    script.onload = () => {
      console.log(`✅ Crisp loaded successfully (Variant: ${config.variant})`)

      // 配置自动回复 (可选)
      configureAutoResponder()

      // 设置用户属性 (可选 - 用于 A/B 测试分析)
      if (config.variant !== 'default') {
        window.$crisp.push([
          'set',
          'session:data',
          [[['ab_test_variant', config.variant]]],
        ])
      }

      // 发送欢迎消息 (可选)
      if (crispAutoResponderConfig.welcomeMessage) {
        // 延迟发送，避免打扰用户
        setTimeout(() => {
          window.$crisp.push([
            'do',
            'message:show',
            ['text', crispAutoResponderConfig.welcomeMessage],
          ])
        }, 3000)
      }
    }

    // 清理函数
    return () => {
      // Crisp 一旦加载就不需要清理，因为它会在整个会话中持续存在
    }
  }, [])
}

/**
 * 配置自动回复功能
 * 注意: 这只是客户端的简单实现
 * 更强大的自动回复应该在 Crisp Dashboard 中配置 Bot 或使用 Crisp API
 */
function configureAutoResponder() {
  if (!window.$crisp || !crispAutoResponderConfig.autoResponses) {
    return
  }

  // 监听用户消息
  window.$crisp.push([
    'on',
    'message:received',
    (message) => {
      if (message.from !== 'user') return

      const userMessage = message.content.toLowerCase()

      // 检查是否匹配自动回复触发词
      for (const autoResponse of crispAutoResponderConfig.autoResponses) {
        const matched = autoResponse.trigger.some((trigger) =>
          userMessage.includes(trigger.toLowerCase()),
        )

        if (matched) {
          // 发送自动回复
          setTimeout(() => {
            window.$crisp.push([
              'do',
              'message:send',
              ['text', autoResponse.response],
            ])
          }, 500)
          break // 只发送第一个匹配的回复
        }
      }
    },
  ])
}

/**
 * Crisp 工具函数
 * 可以在组件中使用这些函数来控制 Crisp
 */
export const CrispUtils = {
  // 打开聊天窗口
  open: () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open'])
    }
  },

  // 关闭聊天窗口
  close: () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:close'])
    }
  },

  // 显示聊天按钮
  show: () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:show'])
    }
  },

  // 隐藏聊天按钮
  hide: () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:hide'])
    }
  },

  // 设置用户信息
  setUser: (email, name, phone, avatar) => {
    if (window.$crisp) {
      if (email) window.$crisp.push(['set', 'user:email', [email]])
      if (name) window.$crisp.push(['set', 'user:nickname', [name]])
      if (phone) window.$crisp.push(['set', 'user:phone', [phone]])
      if (avatar) window.$crisp.push(['set', 'user:avatar', [avatar]])
    }
  },

  // 发送消息
  sendMessage: (message) => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'message:send', ['text', message]])
    }
  },

  // 设置会话数据 (用于追踪和分析)
  setSessionData: (key, value) => {
    if (window.$crisp) {
      window.$crisp.push(['set', 'session:data', [[[key, value]]]])
    }
  },
}
